// Hand-rolled word-level diff for the essay compare view. No dependency: essays are
// short (~650 words), so a token LCS with prefix/suffix trimming is plenty.

export interface DiffRange { start: number; end: number }   // char offsets in the NEW text

interface Token { text: string; start: number; ws: boolean }

function tokenize(text: string): Token[] {
    const out: Token[] = [];
    const re = /\S+|\s+/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text))) out.push({ text: m[0], start: m.index, ws: /^\s+$/.test(m[0]) });
    return out;
}

// Whitespace tokens always compare equal so reflowed spacing/newlines don't read as edits.
function tokEq(a: Token, b: Token): boolean {
    return a.ws && b.ws ? true : a.text === b.text;
}

/**
 * Char ranges in `newText` whose tokens are new or changed vs `oldText`.
 * Adjacent / whitespace-separated ranges are merged.
 */
export function addedRanges(oldText: string, newText: string): DiffRange[] {
    if (oldText === newText) return [];
    const a = tokenize(oldText);
    const b = tokenize(newText);

    // Trim common prefix/suffix token runs — typical revisions touch the middle only.
    let lo = 0;
    while (lo < a.length && lo < b.length && tokEq(a[lo], b[lo])) lo++;
    let hiA = a.length, hiB = b.length;
    while (hiA > lo && hiB > lo && tokEq(a[hiA - 1], b[hiB - 1])) { hiA--; hiB--; }

    const n = hiA - lo, m = hiB - lo;
    const raw: DiffRange[] = [];
    const pushTok = (t: Token) => raw.push({ start: t.start, end: t.start + t.text.length });

    if (m > 0 && (n === 0 || n * m > 6_000_000)) {
        // Nothing to align against, or degenerate size (e.g. a wholesale replace):
        // mark the whole changed middle of the new text.
        raw.push({ start: b[lo].start, end: b[hiB - 1].start + b[hiB - 1].text.length });
    } else if (m > 0) {
        // LCS over the trimmed middle. Token counts stay < 65k, so Uint16 lengths fit.
        const dp = new Uint16Array((n + 1) * (m + 1));
        const at = (i: number, j: number) => i * (m + 1) + j;
        for (let i = n - 1; i >= 0; i--) {
            for (let j = m - 1; j >= 0; j--) {
                dp[at(i, j)] = tokEq(a[lo + i], b[lo + j])
                    ? dp[at(i + 1, j + 1)] + 1
                    : Math.max(dp[at(i + 1, j)], dp[at(i, j + 1)]);
            }
        }
        // Walk the table; any new-text token not on the LCS path is added/changed.
        let i = 0, j = 0;
        while (i < n && j < m) {
            if (tokEq(a[lo + i], b[lo + j])) { i++; j++; }
            else if (dp[at(i + 1, j)] >= dp[at(i, j + 1)]) i++;
            else pushTok(b[lo + j++]);
        }
        while (j < m) pushTok(b[lo + j++]);
    }

    // Merge ranges that touch or are separated only by whitespace in the new text.
    const merged: DiffRange[] = [];
    for (const r of raw.sort((x, y) => x.start - y.start)) {
        const last = merged[merged.length - 1];
        if (last && (r.start <= last.end || /^\s*$/.test(newText.slice(last.end, r.start)))) {
            last.end = Math.max(last.end, r.end);
        } else {
            merged.push({ ...r });
        }
    }
    // Trim range edges to non-whitespace and drop pure-whitespace ranges.
    return merged
        .map(r => {
            let { start, end } = r;
            while (start < end && /\s/.test(newText[start])) start++;
            while (end > start && /\s/.test(newText[end - 1])) end--;
            return { start, end };
        })
        .filter(r => r.end > r.start);
}
